import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { line, Color, Size, formatDateOnly, t, type ColumnDef, type PageSize, type SortState, viewerScopeKey } from '@bms2/shared';
import { Button, Select, TextInput } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { ListViewer } from '../../components/composites/ListViewer';
import { PageModule } from '../../shell/PageModule';
import { useDateFormat } from '../../shell/ViewerZone';
import { post, useApi } from './api';
import { LEADS_PATH } from './LeadStageCards';
import { SiteModal } from './SiteModal';
import { AbsenceModal } from './AbsenceModal';
import styles from './RecordPage.module.css';

export const HYGIENE_ROUTE = '/leads/hygiene';

interface SiteRow {
  siteId: string;
  siteName: string;
  companyId: string;
  companyName: string;
  repName: string | null;
  lastContactAt: string | null;
  days: number | null;
  thresholdDays: number;
  thresholdSource: string;
  relationshipClass: string;
  state: string;
  suppressionReason: string | null;
}

/** The words for an absence's kind (T3434). */
const ABSENCE_KIND_WORDS = { Away: 'hygiene.absence.kind.away', Extended: 'hygiene.absence.kind.extended', DoNotContact: 'hygiene.absence.kind.doNotContact' } as const;

/** One absence as the API lists it (T3434). */
interface AbsenceRow {
  id: string;
  personName: string;
  companyName: string;
  kind: 'Away' | 'Extended' | 'DoNotContact';
  startDate: string;
  expectedReturnDate: string | null;
  source: 'Manual' | 'DetectedFromAutoReply';
  note: string | null;
  confirmed: boolean;
}

interface ViewAnswer {
  sites: SiteRow[];
  noSite: { companyId: string; companyName: string; repName: string | null; daysSinceCreated: number }[];
  suppressed: { targetType: string; targetId: string; name: string; reason: string; setAt: string; setBy: string }[];
  reassignedLeads: { leadId: string; name: string | null; repName: string; endedOn: string }[];
}

/** 🔨 Every table sits inside the List Viewer (piece 4); the sites are one, sorted by days since contact. */
const SITE_COLUMNS: readonly ColumnDef[] = [
  { key: 'companyName', label: 'Company', role: 'primary', type: 'text' },
  { key: 'siteName', label: 'Site', role: 'secondary', type: 'text' },
  { key: 'repName', label: 'Rep', role: 'secondary', type: 'text' },
  { key: 'days', label: 'Days since contact', role: 'secondary', type: 'number' },
  { key: 'threshold', label: 'Threshold · source · class', role: 'secondary', type: 'text' },
  { key: 'state', label: 'State', role: 'status', type: 'status' },
];

/**
 * The manager's follow-up view (piece 20; decisions 0156, 0228): sites by
 * days since contact by rep, the no-Site bucket with + Add clock, the
 * suppressed list beside its reasons, and a departing rep's leads. 🔨 The
 * clock is the Site's and derived; the Company roll-up drives nothing here.
 */
export function HygienePage({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const navigate = useNavigate();
  const { state, reload } = useApi<ViewAnswer>(`${LEADS_PATH}/hygiene`, apiBaseUrl);
  const absences = useApi<{ rows: AbsenceRow[] }>(`${LEADS_PATH}/hygiene/absences`, apiBaseUrl);
  const dateFormat = useDateFormat();
  const [addingAbsence, setAddingAbsence] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [addingClock, setAddingClock] = useState<{ companyId: string; companyName: string } | null>(null);
  const [suppressSite, setSuppressSite] = useState('');
  const [reason, setReason] = useState('');
  const [sort, setSort] = useState<SortState | null>({ key: 'days', direction: 'desc' });
  const [pageSize, setPageSize] = useState<PageSize>(50);
  const run = async () => {
    const answer = await post<{ rowsDue: number; rowsDone: number }>(`${LEADS_PATH}/hygiene/run`, {}, apiBaseUrl);
    setNotice(answer.ok ? `${t('hygiene.ran')} ${answer.data.rowsDone}/${answer.data.rowsDue}` : answer.message);
    if (answer.ok) reload();
  };
  const suppress = async (siteId: string) => {
    const answer = await post(`${LEADS_PATH}/hygiene/suppressions`, { targetType: 'SiteRecord', targetId: siteId, reason }, apiBaseUrl);
    setNotice(answer.ok ? t('settings.saved') : answer.message);
    if (answer.ok) {
      setReason('');
      setSuppressSite('');
      reload();
    }
  };
  /** Confirming keeps the return date the row carries; the act marks the absence as somebody's word (decision 0173). */
  const confirmAbsence = async (row: AbsenceRow) => {
    const answer = await post(`${LEADS_PATH}/hygiene/absences/${encodeURIComponent(row.id)}/confirm`, { expectedReturnDate: row.expectedReturnDate }, apiBaseUrl);
    setNotice(answer.ok ? t('settings.saved') : answer.message);
    if (answer.ok) {
      absences.reload();
      reload();
    }
  };
  const day = (iso: string) => formatDateOnly(`${iso}T00:00:00.000Z`, dateFormat);
  return (
    <PageModule title={t('hygiene.title')} newAction={<Button size={Size.Medium} color={Color.Navy} text={[t('hygiene.runNow')]} actions={[() => void run()]} />}>
      {notice ? <AlertBanner tone={notice.startsWith(t('hygiene.ran')) || notice === t('settings.saved') ? 'good' : 'error'} message={notice} action={{ label: t('modal.dismiss'), onAction: () => setNotice(null) }} /> : null}
      <AsyncStatePanel state={state} emptyAction={t('hygiene.empty')} onRetry={reload}>
        {(view) => (
          <div data-hygiene-view>
            <ListViewer
              viewStore={{ scopeKey: viewerScopeKey('hygiene'), apiBaseUrl }}
              recordType={t('hygiene.title')}
              views={[{ id: 'all', name: t('hygiene.title') }]}
              currentViewId="all"
              onViewChange={() => undefined}
              columns={SITE_COLUMNS}
              rows={view.sites.map((row) => ({ ...row, repName: row.repName ?? t('hygiene.noRep'), days: row.days, threshold: line(row.thresholdDays, '·', row.thresholdSource, '·', row.relationshipClass), state: t(`hygiene.state.${row.state}` as 'hygiene.state.current') }))}
              idOf={(row) => row.siteId}
              read={(row, key) => (row as unknown as Record<string, unknown>)[key]}
              sort={sort}
              onSortChange={setSort}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              emptyAction={t('hygiene.empty')}
              onNavigate={(row) => navigate(`/sites/${row.siteId}`)}
            />
            <div className={styles.actions} data-hygiene-suppress-form>
              <Select size={Size.Large} label={t('hygiene.col.suppress')} value={suppressSite} onChange={setSuppressSite} placeholder={t('edit.none')} options={view.sites.filter((row) => row.state !== 'suppressed').map((row) => ({ value: row.siteId, label: line(row.companyName, '·', row.siteName) }))} dataAttribute={{ 'data-hygiene-suppress-site': 'true' }} />
              <TextInput size={Size.Large} label={t('hygiene.reason')} value={reason} onChange={setReason} name="suppress-reason" />
              <Button size={Size.Small} text={[t('hygiene.suppress')]} actions={[() => void suppress(suppressSite)]} disabled={!suppressSite || !reason.trim()} />
            </div>

            <h3 className={styles.sectionTitle}>{t('hygiene.noSite.title')}</h3>
            <p className={styles.hint}>{t('hygiene.noSite.hint')}</p>
            <ul className={styles.plainList} data-hygiene-no-site={String(view.noSite.length)}>
              {view.noSite.map((row) => (
                <li key={row.companyId} className={styles.actions} data-hygiene-company={row.companyId}>
                  <button type="button" className={styles.linkButton} onClick={() => navigate(`/companies/${row.companyId}`)}>{row.companyName}</button>
                  <span>{row.repName ?? t('hygiene.noRep')}</span>
                  <span>{line(row.daysSinceCreated, t('hygiene.daysSinceCreated'))}</span>
                  <Button size={Size.Small} text={[t('hygiene.addClock')]} actions={[() => setAddingClock({ companyId: row.companyId, companyName: row.companyName })]} />
                </li>
              ))}
            </ul>

            <h3 className={styles.sectionTitle}>{t('hygiene.absences.title')}</h3>
            <p className={styles.hint}>{t('hygiene.absences.hint')}</p>
            <ul className={styles.plainList} data-hygiene-absences={absences.state.kind === 'ready' ? String(absences.state.data.rows.length) : 'loading'}>
              {absences.state.kind === 'ready' && absences.state.data.rows.length === 0 ? <li className={styles.hint}>{t('hygiene.absences.empty')}</li> : null}
              {absences.state.kind === 'ready' ? absences.state.data.rows.map((row) => (
                <li key={row.id} className={styles.absenceRow} data-hygiene-absence={row.id} data-absence-confirmed={String(row.confirmed)}>
                  <span>{line(row.personName, '·', row.companyName)}</span>
                  <span className={styles.absenceFact}>{line(t(ABSENCE_KIND_WORDS[row.kind]), '·', t('hygiene.absence.from'), day(row.startDate), '·', row.expectedReturnDate ? line(t('hygiene.absence.to'), day(row.expectedReturnDate)) : t('hygiene.absence.noReturn'))}</span>
                  <span className={styles.absenceFact}><span className={styles.absenceLabel}>{t('hygiene.absence.label.source')}</span>{row.source === 'Manual' ? t('hygiene.absence.byHand') : t('hygiene.absence.detected')}</span>
                  {row.note ? <span className={styles.absenceFact}><span className={styles.absenceLabel}>{t('hygiene.absence.label.reason')}</span>{row.note}</span> : null}
                  {row.confirmed
                    ? <span className={styles.absenceFact}>{t('hygiene.absence.confirmed')}</span>
                    : <span className={styles.absenceFact}><span className={styles.absenceLabel}>{t('hygiene.absence.unconfirmed')}</span><Button size={Size.Small} text={[t('hygiene.absence.confirm')]} actions={[() => void confirmAbsence(row)]} /></span>}
                </li>
              )) : null}
            </ul>
            <div className={styles.actions}>
              <Button size={Size.Small} text={[t('hygiene.addAbsence')]} actions={[() => setAddingAbsence(true)]} />
            </div>

            <h3 className={styles.sectionTitle}>{t('hygiene.suppressed.title')}</h3>
            <ul className={styles.plainList} data-hygiene-suppressed={String(view.suppressed.length)}>
              {view.suppressed.map((row) => (
                <li key={`${row.targetType}:${row.targetId}`} className={styles.actions}>
                  <span>{line(row.name, '·', row.reason, '·', row.setBy)}</span>
                </li>
              ))}
            </ul>

            <h3 className={styles.sectionTitle}>{t('hygiene.reassigned.title')}</h3>
            <ul className={styles.plainList} data-hygiene-reassigned={String(view.reassignedLeads.length)}>
              {view.reassignedLeads.map((row) => (
                <li key={row.leadId} className={styles.actions}>
                  <button type="button" className={styles.linkButton} onClick={() => navigate(`/leads/${row.leadId}`)}>{row.name ?? row.leadId}</button>
                  <span>{line(row.repName, '·', t('hygiene.endedOn'), row.endedOn)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </AsyncStatePanel>
      <AbsenceModal
        key={addingAbsence ? 'absence-open' : 'absence-closed'}
        open={addingAbsence}
        apiBaseUrl={apiBaseUrl}
        onCancel={() => setAddingAbsence(false)}
        onSaved={() => { setAddingAbsence(false); absences.reload(); reload(); }}
      />
      <SiteModal
        key={addingClock ? `clock-${addingClock.companyId}` : 'clock-closed'}
        open={addingClock !== null}
        companyId={addingClock?.companyId ?? null}
        companyName={addingClock?.companyName ?? null}
        apiBaseUrl={apiBaseUrl}
        onCancel={() => setAddingClock(null)}
        onCreated={() => { setAddingClock(null); reload(); }}
      />
    </PageModule>
  );
}
